// Manages Consent Mode updates via Shopify Customer Privacy API.
// Tag Manager removed — tracking is handled by native Shopify pixels.

export const MAX_PRIVACY_RETRIES = 12
export const PRIVACY_RETRY_DELAY_MS = 100

/**
 * @param {{ analyticsAllowed: boolean, marketingAllowed: boolean, preferencesAllowed: boolean }} flags
 * @returns {Record<string, string>}
 */
export function buildConsentUpdate({
  analyticsAllowed,
  marketingAllowed,
  preferencesAllowed
}) {
  return {
    ad_storage: marketingAllowed ? 'granted' : 'denied',
    ad_user_data: marketingAllowed ? 'granted' : 'denied',
    ad_personalization: marketingAllowed ? 'granted' : 'denied',
    analytics_storage: analyticsAllowed ? 'granted' : 'denied',
    functionality_storage: preferencesAllowed ? 'granted' : 'denied',
    personalization_storage: preferencesAllowed ? 'granted' : 'denied'
  }
}

/**
 * @param {{ privacyRetryScheduled: boolean, privacyRetryAttempts: number, maxRetries?: number }} state
 * @returns {boolean}
 */
export function shouldSchedulePrivacyRetry({
  privacyRetryScheduled,
  privacyRetryAttempts,
  maxRetries = MAX_PRIVACY_RETRIES
}) {
  return !privacyRetryScheduled && privacyRetryAttempts < maxRetries
}

/**
 * @param {import('shopify').CustomerPrivacy | null | undefined} customerPrivacy
 * @returns {{ analyticsAllowed: boolean, marketingAllowed: boolean, preferencesAllowed: boolean }}
 */
export function readConsentFlags(customerPrivacy) {
  if (!customerPrivacy) {
    return {
      analyticsAllowed: false,
      marketingAllowed: false,
      preferencesAllowed: false
    }
  }

  return {
    analyticsAllowed:
      typeof customerPrivacy.analyticsProcessingAllowed === 'function' &&
      customerPrivacy.analyticsProcessingAllowed(),
    marketingAllowed:
      typeof customerPrivacy.marketingAllowed === 'function' &&
      customerPrivacy.marketingAllowed(),
    preferencesAllowed:
      typeof customerPrivacy.preferencesProcessingAllowed === 'function' &&
      customerPrivacy.preferencesProcessingAllowed()
  }
}

let consentModeBootstrapped = false
/** @type {(() => void) | null} */
let consentApplyHandler = null

/** @internal Vitest-only reset for repeated initConsentMode calls in tests. */
export function resetConsentModeBootstrapForTests() {
  if (consentApplyHandler) {
    document.removeEventListener('visitorConsentCollected', consentApplyHandler)
    document.removeEventListener('shopify:consent:updated', consentApplyHandler)
    consentApplyHandler = null
  }
  consentModeBootstrapped = false
}

/**
 * Storefront bootstrap — skipped under Vitest (import.meta.vitest).
 */
export function initConsentMode() {
  if (consentModeBootstrapped) return
  consentModeBootstrapped = true

  window.dataLayer = window.dataLayer || []

  function gtag() {
    window.dataLayer.push(arguments)
  }

  let privacyRetryScheduled = false
  let privacyRetryAttempts = 0

  function applyConsent() {
    const cp = window.Shopify && window.Shopify.customerPrivacy
    if (!cp) {
      if (
        shouldSchedulePrivacyRetry({
          privacyRetryScheduled,
          privacyRetryAttempts,
          maxRetries: MAX_PRIVACY_RETRIES
        })
      ) {
        privacyRetryScheduled = true
        privacyRetryAttempts++
        setTimeout(function () {
          privacyRetryScheduled = false
          applyConsent()
        }, PRIVACY_RETRY_DELAY_MS)
      }
      return
    }
    privacyRetryAttempts = 0

    const flags = readConsentFlags(cp)
    gtag('consent', 'update', buildConsentUpdate(flags))
  }

  consentApplyHandler = applyConsent
  document.addEventListener('visitorConsentCollected', applyConsent)
  document.addEventListener('shopify:consent:updated', applyConsent)
  if (document.readyState !== 'loading') {
    applyConsent()
  } else {
    document.addEventListener('DOMContentLoaded', applyConsent)
  }
}

// Bootstrap storefront : transmet le consentement reel a Google.
if (!import.meta.vitest) {
  initConsentMode()
}
